import { LitElement, html, unsafeCSS } from 'lit'
import { property } from 'lit/decorators.js'
import { SVG_VIEW_SWITCH_EVERYONE_PAIR, SVG_LIST_PAIR } from '@getgo/chameleon-icons'
import { GalleryEvent } from '@/models/gallery-event'
import type { ViewMode } from '@/services/view-mode.service'
import { t } from '@/i18n/translate-utils'
import styles from './gallery-toolbar.styles.scss?inline'

/**
 * Toolbar component for the GoTo Gallery SPA.
 *
 * Renders the select-all checkbox, selection count label, and
 * view-mode toggle buttons (tiles / list).
 *
 * @prop selectedCount - Number of currently selected files.
 * @prop totalFiles    - Total number of files in the gallery.
 * @prop totalSize     - Formatted total size of selected files (e.g. "4.2 MB").
 * @prop viewMode      - Current view mode: `'tiles'` or `'list'`.
 *
 * @fires view-mode-change - `CustomEvent<ViewMode>` when a toggle button is clicked.
 * @fires select-all-toggle - `CustomEvent<void>` when the select-all checkbox changes.
 */
export class GalleryToolbar extends LitElement {
  static readonly tagName = 'gallery-toolbar'
  static styles = unsafeCSS(styles)

  @property({ type: Number }) selectedCount = 0
  @property({ type: Number }) totalFiles = 0
  @property({ type: String }) totalSize = ''
  @property({ type: String }) viewMode: ViewMode = 'tiles'

  private _onSelectAll(): void {
    this.dispatchEvent(new CustomEvent(GalleryEvent.SELECT_ALL_TOGGLE, { bubbles: true, composed: true }))
  }

  private _onToggleTiles(): void {
    this.dispatchEvent(
      new CustomEvent<ViewMode>(GalleryEvent.VIEW_MODE_CHANGE, {
        detail: 'tiles',
        bubbles: true,
        composed: true,
      }),
    )
  }

  private _onToggleList(): void {
    this.dispatchEvent(
      new CustomEvent<ViewMode>(GalleryEvent.VIEW_MODE_CHANGE, {
        detail: 'list',
        bubbles: true,
        composed: true,
      }),
    )
  }

  render() {
    const allSelected = this.selectedCount > 0 && this.selectedCount === this.totalFiles

    const checkbox = html`<chameleon-checkbox
      .checked=${allSelected}
      aria-label=${t('gallery.toolbar.selectAll', 'Select all')}
      @change=${this._onSelectAll}
    ></chameleon-checkbox>`

    return html`
      <div class="toolbar" role="toolbar">
        <div class="toolbar-left">
          ${checkbox}
          <span class="selection-label">
            ${t('gallery.toolbar.selected', '{{count}} selected ({{size}})', {
              count: this.selectedCount,
              size: this.totalSize,
            })}
          </span>
        </div>
        <div class="toolbar-right">
          <chameleon-toggle-button
            id="toggle-tiles-button"
            size="medium"
            .iconPair=${SVG_VIEW_SWITCH_EVERYONE_PAIR}
            ?selected=${this.viewMode === 'tiles'}
            ?background-fill=${this.viewMode === 'tiles'}
            aria-label=${t('gallery.view.tiles', 'Tile view')}
            @click=${this._onToggleTiles}
          ></chameleon-toggle-button>
          <chameleon-tooltip trigger-id="toggle-tiles-button">
            ${t('gallery.view.tiles', 'Tile view')}
          </chameleon-tooltip>
          <chameleon-toggle-button
            id="toggle-list-button"
            size="medium"
            .iconPair=${SVG_LIST_PAIR}
            ?selected=${this.viewMode === 'list'}
            ?background-fill=${this.viewMode === 'list'}
            aria-label=${t('gallery.view.list', 'List view')}
            @click=${this._onToggleList}
          ></chameleon-toggle-button>
          <chameleon-tooltip trigger-id="toggle-list-button">
            ${t('gallery.view.list', 'List view')}
          </chameleon-tooltip>
        </div>
      </div>
    `
  }
}
